import React from 'react'
import PropTypes from 'prop-types'
import { IconCircleCheck, IconInfo } from '@edulastic/icons'

import { getConfidenceScoreForStandard } from '../utils/AIQuestionAssistance'
import {
  StyledAdditionalStandardsModal,
  StyledSuggestionsList,
  StyledSuggestionRow,
  StyledSuggestionRowHeader,
  StyledSuggestionStatusContainer,
  StyledCurrentlyUsedLabel,
  StyledRemoveAction,
  StyledUseSuggestionButton,
  StyledBrowseFooter,
  StyledBrowseFooterText,
  StyledBrowseStandardsLink,
  StyledStandardIdentifier,
  StyledStandardIdentifierGroup,
  StyledStandardDescription,
  StyledStandardLowConfidenceWarning,
  StyledStandardSetTooltip,
  StyledStandardTooltipContainer,
  StyledStandardTooltipWrapper,
} from './styled'

const StandardsSuggestedModal = ({
  visible,
  onClose,
  suggestions,
  standardsMetadata,
  selectedStandards,
  onUseSuggestion,
  onRemoveStandard,
  onBrowseStandards,
  lowConfidenceThreshold,
  readOnly,
}) => {
  const suggestedStandardIds = new Set(suggestions.map((s) => s._id))
  const userSelectedStandards = selectedStandards.filter(
    (s) => !suggestedStandardIds.has(s._id)
  )
  // In read-only mode the item's existing standards are passed via `suggestions`
  const rows = readOnly
    ? suggestions
    : [...userSelectedStandards, ...suggestions]

  const renderIdentifier = (standard) => (
    <StyledStandardIdentifierGroup>
      <StyledStandardIdentifier
        data-cy="pearAssistSuggestionIdentifier"
        isOutOfPreferredSet={readOnly && standard.isOutOfPreferredSet}
      >
        {standard.identifier}
      </StyledStandardIdentifier>
      {readOnly && standard.isOutOfPreferredSet && (
        <StyledStandardSetTooltip
          title={
            <StyledStandardTooltipContainer>
              This standard belongs to &quot;{standard?.standardSetName || ''}
              &quot; which isn&apos;t currently included in your interested set.
            </StyledStandardTooltipContainer>
          }
          data-cy="pearAssistSuggestionOutOfSetInfo"
        >
          <StyledStandardTooltipWrapper>
            <IconInfo
              width="14px"
              height="14px"
              fill="#919191"
              aria-hidden="true"
            />
          </StyledStandardTooltipWrapper>
        </StyledStandardSetTooltip>
      )}
    </StyledStandardIdentifierGroup>
  )

  const renderCurrentlyUsed = () => (
    <StyledCurrentlyUsedLabel data-cy="pearAssistSuggestionCurrentlyUsed">
      <IconCircleCheck
        color="#20845e"
        checkColor="#ffffff"
        width="16px"
        height="16px"
        aria-hidden="true"
      />
      Currently used
    </StyledCurrentlyUsedLabel>
  )

  return (
    <StyledAdditionalStandardsModal
      visible={visible}
      title="Standards Suggested"
      footer={null}
      centered
      onCancel={onClose}
      width={550}
      maskClosable
      destroyOnClose
    >
      <StyledSuggestionsList data-cy="pearAssistStandardSuggestionsList">
        {rows.map((standard) => {
          const isSelected = selectedStandards.some(
            (s) => s._id === standard._id
          )
          const confidence = getConfidenceScoreForStandard(
            standard,
            standardsMetadata
          )
          const showLowConfidence =
            !readOnly &&
            typeof confidence === 'number' &&
            confidence < lowConfidenceThreshold

          return (
            <StyledSuggestionRow
              key={standard._id}
              data-cy={`pearAssistSuggestionRow-${standard.identifier}`}
            >
              <StyledSuggestionRowHeader>
                {renderIdentifier(standard)}
                <StyledSuggestionStatusContainer>
                  {readOnly && renderCurrentlyUsed()}
                  {!readOnly && isSelected && (
                    <>
                      {renderCurrentlyUsed()}
                      <StyledRemoveAction
                        type="button"
                        data-cy="pearAssistSuggestionRemove"
                        onClick={() => onRemoveStandard(standard)}
                      >
                        Remove
                      </StyledRemoveAction>
                    </>
                  )}
                  {!readOnly && !isSelected && (
                    <StyledUseSuggestionButton
                      isGhost
                      data-cy="pearAssistSuggestionUse"
                      onClick={() => onUseSuggestion(standard)}
                    >
                      Use this suggestion
                    </StyledUseSuggestionButton>
                  )}
                </StyledSuggestionStatusContainer>
              </StyledSuggestionRowHeader>
              <StyledStandardDescription data-cy="pearAssistSuggestionDescription">
                {standard.description}
              </StyledStandardDescription>
              {showLowConfidence && (
                <StyledStandardLowConfidenceWarning
                  data-cy="pearAssistSuggestionLowConfidence"
                  role="status"
                >
                  Low confidence on standards suggested
                </StyledStandardLowConfidenceWarning>
              )}
            </StyledSuggestionRow>
          )
        })}
      </StyledSuggestionsList>

      {!readOnly && (
        <StyledBrowseFooter>
          <StyledBrowseFooterText>
            Standards not matching, Browse and add standards as per your
            preference.
          </StyledBrowseFooterText>
          <StyledBrowseStandardsLink
            type="button"
            data-cy="pearAssistBrowseStandards"
            onClick={onBrowseStandards}
          >
            + Add Standards
          </StyledBrowseStandardsLink>
        </StyledBrowseFooter>
      )}
    </StyledAdditionalStandardsModal>
  )
}

StandardsSuggestedModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  suggestions: PropTypes.array,
  standardsMetadata: PropTypes.array,
  selectedStandards: PropTypes.array,
  onUseSuggestion: PropTypes.func,
  onRemoveStandard: PropTypes.func,
  onBrowseStandards: PropTypes.func,
  lowConfidenceThreshold: PropTypes.number,
  readOnly: PropTypes.bool,
}

StandardsSuggestedModal.defaultProps = {
  suggestions: [],
  standardsMetadata: [],
  selectedStandards: [],
  lowConfidenceThreshold: 0.7,
  readOnly: false,
  onUseSuggestion: () => {},
  onRemoveStandard: () => {},
  onBrowseStandards: () => {},
}

export default StandardsSuggestedModal
